import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon, ChevronDown } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";

const Header = () => {
  const { isDarkMode, toggleTheme, isAnimating } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

 const navigationItems = [
  { name: "Home", href: "/" },

  {
    name: "About FSGU",
    items: [
      { name: "Executive Committee", href: "/about/executive" },
      { name: "FSGU News", href: "/news" },
      {
        name: "FSGU Constitution",
        href: "/FSGU-constitution.pdf",
        target: "_blank",
        rel: "noopener noreferrer",
      },
      {
        name: "FSGU Financial Policy",
        href: "/FSGU - Financial policy - 02.18.2026.pdf",
        target: "_blank",
        rel: "noopener noreferrer",
      },
           {
        name: "FSGU Prize Money Policy",
                href: "/FSGU-prize-money-policy-2026.pdf",
        target: "_blank",
        rel: "noopener noreferrer",
      },
      { name: "GolfRSA News", href: "/golfRSA-news" },
      {
        name: "GolfRSA Transformation Charter",
        href: "/GolfRSA-Transformation-Charter.pdf",
        target: "_blank",
        rel: "noopener noreferrer",
      },
      {
        name: "Anti Doping - Prohibited Substances",
        href: "/anti-doping-prohibited-list-2025.pdf",
        target: "_blank",
        rel: "noopener noreferrer",
      },
      {
        name: "GolfRSA Safeguarding Policy",
        href: "/golfRSA-safeguarding-policy.pdf",
        target: "_blank",
        rel: "noopener noreferrer",
      },
      {
        name: "Rules of Golf",
        href: "https://www.randa.org/rog/the-rules-of-golf",
        target: "_blank",
        rel: "noopener noreferrer",
      },
    ],
  },
  { name: "FSGU Clubs", href: "/clubs" },
  { name: "Contact FSGU", href: "/contact" },
];

  const rightNavigationItems = [
    { name: "Tournaments", href: "/tournaments" },
    { name: "Fixtures", href: "/fixtures" },
    { name: "Results", href: "/results" },
    { name: "Order of Merit", href: "/order-of-merit" },
    { name: "Admin Login", href: "/admin/login" },
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openDropdown]);

  // Check if link is active
  const isActiveLink = (href) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(href);
  };

  // Check if dropdown has active item
  const hasActiveItem = (items) => {
    return items?.some((item) => isActiveLink(item.href));
  };

  const handleDropdownClick = (e, index) => {
    e.stopPropagation();
    setOpenDropdown(openDropdown === index ? null : index);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled
          ? `backdrop-blur-xl border-b shadow-soft ${isDarkMode
            ? "bg-fsgu-gray-900/95 border-fsgu-gray-700/50"
            : "bg-fsgu-white/95 border-fsgu-silver-200/50"
          }`
          : "bg-transparent"
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile: Use grid for perfect centering, Desktop: Use flex */}
        <div className="grid grid-cols-[52px_1fr_52px] lg:flex lg:items-center lg:justify-between h-16">
          {/* Mobile Left - Theme Toggle */}
          <div className="flex items-center justify-start">
            <button
              onClick={toggleTheme}
              disabled={isAnimating}
              className={`lg:hidden p-2 rounded-lg transition-all duration-500 shadow-soft backdrop-blur-sm border ${isDarkMode
                  ? "bg-fsgu-gray-700/50 hover:bg-fsgu-green-500/10 border-fsgu-gray-600/50 hover:border-fsgu-green-500/30"
                  : "bg-fsgu-silver-100/50 hover:bg-fsgu-green-500/5 border-fsgu-silver-200/50 hover:border-fsgu-green-500/30"
                }`}
              aria-label="Toggle theme"
            >
              <div className="relative w-5 h-5">
                <Sun
                  className={`absolute inset-0 w-5 h-5 transition-all duration-500 text-fsgu-gold-500 ${isDarkMode
                      ? "opacity-0 rotate-180 scale-0"
                      : "opacity-100 rotate-0 scale-100"
                    }`}
                />

                <Moon
                  className={`absolute inset-0 w-5 h-5 transition-all duration-500 text-fsgu-blue-400 ${isDarkMode
                      ? "opacity-100 rotate-0 scale-100"
                      : "opacity-0 rotate-180 scale-0"
                    }`}
                />
              </div>
            </button>
          </div>

          {/* Left Navigation - Desktop Only */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item, index) => {
              // Regular link (no dropdown)
              if (!item.items) {
                return (
                  <Link
                    key={index}
                    to={item.href}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-105 ${isActiveLink(item.href)
                        ? isDarkMode
                          ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                          : "text-fsgu-green-500 bg-fsgu-green-500/10"
                        : isDarkMode
                          ? "text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-800"
                          : "text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-100"
                      }`}
                  >
                    {item.name}
                  </Link>
                );
              }

              // Dropdown menu
              return (
                <div key={index} className="relative">
                  <button
                    onClick={(e) => handleDropdownClick(e, index)}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${hasActiveItem(item.items) || openDropdown === index
                        ? isDarkMode
                          ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                          : "text-fsgu-green-500 bg-fsgu-green-500/10"
                        : isDarkMode
                          ? "text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-800"
                          : "text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-100"
                      }`}
                  >
                    <span>{item.name}</span>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-300 ${openDropdown === index ? "rotate-180" : ""
                        }`}
                    />
                  </button>

                  {/* Dropdown Menu */}
                  {openDropdown === index && (
                    <div
                      className={`absolute top-full left-0 mt-2 w-56 rounded-lg shadow-lg border overflow-hidden ${isDarkMode
                          ? "bg-fsgu-gray-800 border-fsgu-gray-700"
                          : "bg-fsgu-white border-fsgu-silver-200"
                        }`}
                    >
                      {item.items.map((subItem, subIndex) =>
                        subItem.target ? (
                          <a
                            key={subIndex}
                            href={subItem.href}
                            target={subItem.target}
                                                     rel={subItem.rel}
                            download={subItem.download}
                            className={`block px-4 py-3 text-sm transition-colors duration-300 ${isActiveLink(subItem.href)
                                ? isDarkMode
                                  ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                                  : "text-fsgu-green-500 bg-fsgu-green-500/10"
                                : isDarkMode
                                  ? "text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-700"
                                  : "text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-50"
                              }`}
                          >
                            {subItem.name}
                          </a>
                        ) : (
                          <Link
                            key={subIndex}
                            to={subItem.href}
                            className={`block px-4 py-3 text-sm transition-colors duration-300 ${isActiveLink(subItem.href)
                                ? isDarkMode
                                  ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                                  : "text-fsgu-green-500 bg-fsgu-green-500/10"
                                : isDarkMode
                                  ? "text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-700"
                                  : "text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-50"
                              }`}
                          >
                            {subItem.name}
                          </Link>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Centered Logo - Perfectly centered on mobile with grid, natural on desktop */}
          <Link
            to="/"
            className="flex items-center justify-center lg:relative lg:left-auto cursor-pointer group"
          >
            <img
              src={isDarkMode ? "/fsgu-dark.png" : "/fsgu-light.png"}
              alt="FSGU Logo"
              className="h-16 w-auto transition-all duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Right Navigation + Theme Toggle (Desktop) + Mobile Menu */}
          <div className="flex items-center justify-end space-x-2">
            {/* Right Navigation Items - Desktop Only */}
            <nav className="hidden lg:flex items-center space-x-1">
              {rightNavigationItems.map((item, index) => {
                const rightIndex = 1000 + index; // Offset to avoid conflicts

                // Regular link (no dropdown)
                if (!item.items) {
                  // Admin Login gets button styling
                  if (item.name === "Admin Login") {
                    return (
                      <Link
                        key={rightIndex}
                        to={item.href}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-105 ${isDarkMode
                            ? "bg-fsgu-green-500/20 text-fsgu-green-400 hover:bg-fsgu-green-500/30 border border-fsgu-green-500/30"
                            : "bg-fsgu-green-500/10 text-fsgu-green-600 hover:bg-fsgu-green-500/20 border border-fsgu-green-500/30"
                          }`}
                      >
                        {item.name}
                      </Link>
                    );
                  }

                  // Regular links
                  return (
                    <Link
                      key={rightIndex}
                      to={item.href}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-105 ${isActiveLink(item.href)
                          ? isDarkMode
                            ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                            : "text-fsgu-green-500 bg-fsgu-green-500/10"
                          : isDarkMode
                            ? "text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-800"
                            : "text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-100"
                        }`}
                    >
                      {item.name}
                    </Link>
                  );
                }

                // Dropdown menu
                return (
                  <div key={rightIndex} className="relative">
                    <button
                      onClick={(e) => handleDropdownClick(e, rightIndex)}
                      className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${hasActiveItem(item.items) || openDropdown === rightIndex
                          ? isDarkMode
                            ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                            : "text-fsgu-green-500 bg-fsgu-green-500/10"
                          : isDarkMode
                            ? "text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-800"
                            : "text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-100"
                        }`}
                    >
                      <span>{item.name}</span>
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-300 ${openDropdown === rightIndex ? "rotate-180" : ""
                          }`}
                      />
                    </button>

                    {/* Dropdown Menu */}
                    {openDropdown === rightIndex && (
                      <div
                        className={`absolute top-full right-0 mt-2 w-56 rounded-lg shadow-lg border overflow-hidden ${isDarkMode
                            ? "bg-fsgu-gray-800 border-fsgu-gray-700"
                            : "bg-fsgu-white border-fsgu-silver-200"
                          }`}
                      >
                        {item.items.map((subItem, subIndex) =>
                          subItem.target ? (
                            <a
                              key={subIndex}
                              href={subItem.href}
                              target={subItem.target}
                                                         rel={subItem.rel}
                              download={subItem.download}
                              className={`block px-4 py-3 text-sm transition-colors duration-300 ${isActiveLink(subItem.href)
                                  ? isDarkMode
                                    ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                                    : "text-fsgu-green-500 bg-fsgu-green-500/10"
                                  : isDarkMode
                                    ? "text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-700"
                                    : "text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-50"
                                }`}
                            >
                              {subItem.name}
                            </a>
                          ) : (
                            <Link
                              key={subIndex}
                              to={subItem.href}
                              className={`block px-4 py-3 text-sm transition-colors duration-300 ${isActiveLink(subItem.href)
                                  ? isDarkMode
                                    ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                                    : "text-fsgu-green-500 bg-fsgu-green-500/10"
                                  : isDarkMode
                                    ? "text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-700"
                                    : "text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-50"
                                }`}
                            >
                              {subItem.name}
                            </Link>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Theme Toggle - Desktop Only */}
            <button
              onClick={toggleTheme}
              disabled={isAnimating}
              className={`hidden lg:block p-2 rounded-lg transition-all duration-500 hover:scale-105 shadow-soft backdrop-blur-sm border ${isDarkMode
                  ? "bg-fsgu-gray-700/50 hover:bg-fsgu-green-500/10 border-fsgu-gray-600/50 hover:border-fsgu-green-500/30"
                  : "bg-fsgu-silver-100/50 hover:bg-fsgu-green-500/5 border-fsgu-silver-200/50 hover:border-fsgu-green-500/30"
                }`}
              aria-label="Toggle theme"
            >
              <div className="relative w-5 h-5">
                <Sun
                  className={`absolute inset-0 w-5 h-5 transition-all duration-500 text-fsgu-gold-500 ${isDarkMode
                      ? "opacity-0 rotate-180 scale-0"
                      : "opacity-100 rotate-0 scale-100"
                    }`}
                />

                <Moon
                  className={`absolute inset-0 w-5 h-5 transition-all duration-500 text-fsgu-blue-400 ${isDarkMode
                      ? "opacity-100 rotate-0 scale-100"
                      : "opacity-0 rotate-180 scale-0"
                    }`}
                />
              </div>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-all duration-300 ${isDarkMode
                  ? "text-fsgu-silver-300 hover:bg-fsgu-gray-800"
                  : "text-fsgu-silver-600 hover:bg-fsgu-silver-100"
                }`}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden transition-all duration-300 ease-in-out overflow-hidden ${isMobileMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
          }`}
      >
        <nav
          className={`px-4 py-4 space-y-1 border-t ${isDarkMode
              ? "bg-fsgu-gray-900/95 border-fsgu-gray-700/50"
              : "bg-fsgu-white/95 border-fsgu-silver-200/50"
            }`}
        >
          {/* Left Navigation Items */}
          {navigationItems.map((item, index) => {
            // Regular link (no dropdown)
            if (!item.items) {
              return (
                <Link
                  key={index}
                  to={item.href}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 ${isActiveLink(item.href)
                      ? isDarkMode
                        ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                        : "text-fsgu-green-500 bg-fsgu-green-500/10"
                      : isDarkMode
                        ? "text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-800"
                        : "text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-100"
                    }`}
                >
                  {item.name}
                </Link>
              );
            }

            // Mobile dropdown
            return (
              <div key={index}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdown(openDropdown === index ? null : index);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 ${hasActiveItem(item.items) || openDropdown === index
                      ? isDarkMode
                        ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                        : "text-fsgu-green-500 bg-fsgu-green-500/10"
                      : isDarkMode
                        ? "text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-800"
                        : "text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-100"
                    }`}
                >
                  <span>{item.name}</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-300 ${openDropdown === index ? "rotate-180" : ""
                      }`}
                  />
                </button>

                {/* Mobile Dropdown Items */}
                {openDropdown === index && (
                  <div className="mt-1 ml-4 space-y-1">
                    {item.items.map((subItem, subIndex) =>
                      subItem.target ? (
                        <a
                          key={subIndex}
                          href={subItem.href}
                          target={subItem.target}
                          rel={subItem.rel}
                          className={`block px-4 py-2 rounded-lg text-sm transition-all duration-300 ${isActiveLink(subItem.href)
                              ? isDarkMode
                                ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                                : "text-fsgu-green-500 bg-fsgu-green-500/10"
                              : isDarkMode
                                ? "text-fsgu-silver-400 hover:text-fsgu-green-300 hover:bg-fsgu-gray-800"
                                : "text-fsgu-silver-500 hover:text-fsgu-green-600 hover:bg-fsgu-silver-100"
                            }`}
                        >
                          {subItem.name}
                        </a>
                      ) : (
                        <Link
                          key={subIndex}
                          to={subItem.href}
                          className={`block px-4 py-2 rounded-lg text-sm transition-all duration-300 ${isActiveLink(subItem.href)
                              ? isDarkMode
                                ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                                : "text-fsgu-green-500 bg-fsgu-green-500/10"
                              : isDarkMode
                                ? "text-fsgu-silver-400 hover:text-fsgu-green-300 hover:bg-fsgu-gray-800"
                                : "text-fsgu-silver-500 hover:text-fsgu-green-600 hover:bg-fsgu-silver-100"
                            }`}
                        >
                          {subItem.name}
                        </Link>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Right Navigation Items */}
          {rightNavigationItems.map((item, index) => {
            const rightIndex = 1000 + index;

            // Regular link (no dropdown)
            if (!item.items) {
              // Admin Login gets button styling
              if (item.name === "Admin Login") {
                return (
                  <Link
                    key={rightIndex}
                    to={item.href}
                    className={`block px-4 py-3 rounded-lg text-base font-medium text-center transition-all duration-300 ${isDarkMode
                        ? "bg-fsgu-green-500/20 text-fsgu-green-400 hover:bg-fsgu-green-500/30 border border-fsgu-green-500/30"
                        : "bg-fsgu-green-500/10 text-fsgu-green-600 hover:bg-fsgu-green-500/20 border border-fsgu-green-500/30"
                      }`}
                  >
                    {item.name}
                  </Link>
                );
              }

              // Regular links
              return (
                <Link
                  key={rightIndex}
                  to={item.href}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 ${isActiveLink(item.href)
                      ? isDarkMode
                        ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                        : "text-fsgu-green-500 bg-fsgu-green-500/10"
                      : isDarkMode
                        ? "text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-800"
                        : "text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-100"
                    }`}
                >
                  {item.name}
                </Link>
              );
            }

            // Mobile dropdown
            return (
              <div key={rightIndex}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdown(
                      openDropdown === rightIndex ? null : rightIndex
                    );
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 ${hasActiveItem(item.items) || openDropdown === rightIndex
                      ? isDarkMode
                        ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                        : "text-fsgu-green-500 bg-fsgu-green-500/10"
                      : isDarkMode
                        ? "text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-800"
                        : "text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-100"
                    }`}
                >
                  <span>{item.name}</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-300 ${openDropdown === rightIndex ? "rotate-180" : ""
                      }`}
                  />
                </button>

                {/* Mobile Dropdown Items */}
                {openDropdown === rightIndex && (
                  <div className="mt-1 ml-4 space-y-1">
                    {item.items.map((subItem, subIndex) =>
                      subItem.target ? (
                        <a
                          key={subIndex}
                          href={subItem.href}
                          target={subItem.target}
                          rel={subItem.rel}
                          className={`block px-4 py-2 rounded-lg text-sm transition-all duration-300 ${isActiveLink(subItem.href)
                              ? isDarkMode
                                ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                                : "text-fsgu-green-500 bg-fsgu-green-500/10"
                              : isDarkMode
                                ? "text-fsgu-silver-400 hover:text-fsgu-green-300 hover:bg-fsgu-gray-800"
                                : "text-fsgu-silver-500 hover:text-fsgu-green-600 hover:bg-fsgu-silver-100"
                            }`}
                        >
                          {subItem.name}
                        </a>
                      ) : (
                        <Link
                          key={subIndex}
                          to={subItem.href}
                          className={`block px-4 py-2 rounded-lg text-sm transition-all duration-300 ${isActiveLink(subItem.href)
                              ? isDarkMode
                                ? "text-fsgu-green-400 bg-fsgu-green-500/10"
                                : "text-fsgu-green-500 bg-fsgu-green-500/10"
                              : isDarkMode
                                ? "text-fsgu-silver-400 hover:text-fsgu-green-300 hover:bg-fsgu-gray-800"
                                : "text-fsgu-silver-500 hover:text-fsgu-green-600 hover:bg-fsgu-silver-100"
                            }`}
                        >
                          {subItem.name}
                        </Link>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </header>
  );
};

export default Header;
